import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { determineCustomerType } from '../../../../utils/customerTypeDetection';
import { subscribeToProduct } from '../../subscriptions/services/SubscriptionsService';
import { fetchProduct } from '../services/ProductsService';
import { fetchPricesByProduct } from '../../prices/services/PricesService';
import { fetchApplicationProducts } from '../services/ApplicationProductsService';
import { useEnableAccountApplicationMutation } from '../../../accounts-management/account-applications/hooks/useAccountApplicationsQuery';

export const useSubscribeToProduct = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const productId = searchParams.get('productId');
    const returnTo = searchParams.get('returnTo');

    const { userAccount: account } = useUserAccount();

    const { data: product } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => fetchProduct(productId as string),
        enabled: !!productId,
    });

    const { data: prices } = useQuery({
        queryKey: ['prices', productId],
        queryFn: () => fetchPricesByProduct(productId as string),
        enabled: !!productId,
    });

    const [loading, setLoading] = useState(false);

    // FNP-2070: the one enable path. It reactivates a Disabled record in place
    // (console-api, FNP-902) and invalidates the account's real list key. The old
    // check-then-create skipped ANY existing record, so a Disabled one stayed off.
    const enableMutation = useEnableAccountApplicationMutation();

    const ensureAccountApplication = async (accountId: string, applicationId: string) => {
        try {
            await enableMutation.mutateAsync({ accountId, applicationId });
        } catch (e) {
            console.error('Failed to enable account-application:', e);
            notifications.show({
                title: 'Application not enabled',
                message: `Subscribed, but the application could not be enabled: ${(e as Error).message}`,
                color: 'orange'
            });
        }
    };

    const handleSubscribe = async () => {
        if (!product) {
            console.error('No product found');
            return;
        }

        const customerId = account?.customerId;

        if (!customerId) {
            console.error('No customer ID available');
            notifications.show({
                title: 'Billing Setup Required',
                message: 'Please set up your billing profile before subscribing. Visit the Subscriptions page to get started.',
                color: 'orange'
            });
            navigate('/subscriptions');
            return;
        }

        setLoading(true);
        try {
            const customerType = determineCustomerType();
            const price = prices?.[0];

            if (!price) {
                console.error('No price found for product');
                notifications.show({
                    title: 'Error',
                    message: 'No pricing information available',
                    color: 'red'
                });
                setLoading(false);
                return;
            }

            // Check if payment is required
            if (customerType === 'direct' && !product.freeTrial && Number(price.amount || 0) > 0) {
                navigate(`/billing/providers?productId=${product.id}&priceId=${price.id}&returnTo=${encodeURIComponent(returnTo || '/products')}`);
                return;
            }

            // Create subscription directly (free trial or marketplace customer)
            const result = await subscribeToProduct(customerId, price.id, 1, queryClient);

            if (result) {
                // Auto-enable the application for the user's account
                if (account?.id && productId) {
                    const appProducts = await fetchApplicationProducts();
                    const mapping = appProducts?.find(ap => ap.productId === productId);
                    if (mapping?.applicationId) {
                        await ensureAccountApplication(account.id, mapping.applicationId);
                    }
                }

                await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
                await queryClient.invalidateQueries({ queryKey: ['products'] });

                notifications.show({
                    title: 'Success',
                    message: `Successfully subscribed to ${product.name}`,
                    color: 'green'
                });

                if (returnTo) {
                    navigate(decodeURIComponent(returnTo));
                } else {
                    navigate('/products');
                }
            }
        } catch (error) {
            console.error('Subscription error:', error);
            notifications.show({
                title: 'Error',
                message: 'Failed to create subscription',
                color: 'red'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return {
        product: product ?? null,
        prices: prices ?? [],
        loading,
        handleSubscribe,
        handleCancel
    };
};
